/*
 * Atolyewolf – charm önizleme (test)
 *
 * <aw-charm-preview> (snippets/aw-charm-preview.liquid) seçili charm'ları boş bileklik fotoğrafının ön ipine dizer:
 * - İp/metal: varyant seçenekleri (İp Rengi + Charm Rengi) → kombinasyon; her kombinasyonun kendi boş bileklik görseli (varyant
 *   görseli) var, galeride yalnızca seçili kombinasyonun görseli gösterilir. Yeni ip rengi tema değişikliği istemez: kırmızı/
 *   siyah/beyaz dışındaki renkler renkli-<metal> sprite'ını kullanır; standart dışı kadrajda ip eğrisi görselden ölçülür.
 * - 1–4. charm: charm seçici bloğunun durumu (window.__awExtraLetters.state.letters[1..4]); 1. charm zorunlu ve
 *   fiyata dahil, seçim/sepete ekleme kuralları blokta (liquid_qDpfd6). Sayfa boş bileklikle açılır.
 * Aynı çizim ürün galerisindeki boş bileklik görseline (masaüstünde galeri yapışkan) ve telefon/tablette ürün görseli
 * ekrandan çıkınca başlığın altında beliren sabit önizleme şeridine yapılır. Koordinatlar 2000×2000 px fotoğraf düzleminde; ip eğrisi polinomla (coef) verilir.
 */

(() => {
  const BASE_MARKER = 'aw-charm-bos-bileklik-';

  class AwCharmPreview extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      try {
        this.config = JSON.parse(this.querySelector('[data-aw-cp-config]').textContent);
        this.variantCombos = JSON.parse(this.querySelector('[data-aw-cp-variants]').textContent);
        this.assets = JSON.parse(this.querySelector('[data-aw-cp-assets]').textContent);
        this.cords = JSON.parse(this.querySelector('[data-aw-cp-cords]')?.textContent || '{}');
      } catch (error) {
        console.warn('[aw-charm-preview] config error', error);
        return;
      }

      this.section = this.closest('.shopify-section') || document;
      this.fits = {};
      this.layers = [...this.querySelectorAll('[data-aw-cp-layer]')];
      this.floatElement = this.querySelector('[data-aw-cp-float]');

      const schedule = () => this.scheduleRender();

      this.section.addEventListener('click', schedule, { signal });
      document.addEventListener('variant:change', schedule, { signal });
      window.addEventListener('resize', () => this.updateLayout(), { signal });
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateFloat();
        });
      }, { passive: true, signal });

      // Charm seçici bloğu seçimleri aria-pressed/hidden ile boyar → değişiklikleri izle
      this.extraObserver = new MutationObserver(schedule);
      this.section.querySelectorAll('[data-aw-extra]').forEach((element) => {
        this.extraObserver.observe(element, { subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'hidden'] });
      });

      this.resizeObserver = new ResizeObserver(() => this.positionGalleryLayer());

      this.render();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.extraObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.galleryLayer?.remove();
    }

    scheduleRender() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);

      // Arka plandaki sekmede requestAnimationFrame çalışmaz
      if (document.hidden) {
        this.timer = setTimeout(() => this.render(), 0);
      } else {
        this.frame = requestAnimationFrame(() => this.render());
      }
    }

    get form() {
      return this.section.querySelector('product-form form[action*="/cart/add"]') || this.section.querySelector('form[action*="/cart/add"]');
    }

    // Seçili varyantın ip/metal kombinasyonu (ör. "siyah-gumus", yeni ip renklerinde "sari-gumus")
    combo() {
      const variantId = this.form ? new FormData(this.form).get('id') : null;
      const combo = variantId ? this.variantCombos[String(variantId)] : null;
      return combo && this.assets[combo] && this.geometry(combo) ? combo : Object.keys(this.config.combos)[0];
    }

    // Sprite boyutu + ip eğrisi + kesimler. Kendi fotoğraf seti olmayan ip renkleri renkli-<metal> kullanır
    // ("like": kesimlerin alındığı kombinasyon); farklı kadrajlı görselde ip eğrisi görselden ölçülür (fitCord)
    geometry(combo) {
      const combos = this.config.combos;
      const entry = combos[combo] || combos[`renkli-${combo.split('-').pop()}`];

      if (!entry) return null;
      if (!entry.like) return entry;

      const asset = this.assets[combo];
      const measured = !combos[combo] && asset?.base && !(asset.marker || '').startsWith(BASE_MARKER);
      const fitted = measured ? this.fits[asset.base]?.coef : null;
      return { ...combos[entry.like], coef: fitted || entry.coef || combos[entry.like].coef };
    }

    /* Görsel ölçümü: yeni ip rengi görseli standart kadrajda değilse ön ipin alt kenarı ve ip rengi görselden okunur */

    analyze(combo) {
      this.analysis = this.analysis || {};
      const src = this.assets[combo]?.base;

      if (!src) return Promise.resolve(null);

      if (!this.analysis[src]) {
        this.analysis[src] = new Promise((resolve) => {
          const image = new Image();
          image.crossOrigin = 'anonymous';
          image.onload = () => {
            try {
              resolve(this.measure(image));
            } catch (error) {
              resolve(null);
            }
          };
          image.onerror = () => resolve(null);
          image.src = src;
        });
      }

      return this.analysis[src];
    }

    // 2000×2000 düzleminde x 480–1520 arası sütunlarda alttan yukarı ilk ip pikseli = ön ipin alt kenarı;
    // charm merkezi kenarın 12.5 px üstünde (kırmızı ip fotoğraflarında ölçüldü). Eğri: 2. derece en küçük kareler.
    measure(image) {
      const size = 1000;
      const scale = 2000 / size;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(image, 0, 0, size, size);
      const data = context.getImageData(0, 0, size, size).data;
      const points = [];
      const colors = [];

      for (let x = 240; x <= 760; x += 10) {
        for (let y = 760; y >= 540; y--) {
          const i = (y * size + x) * 4;

          if (765 - data[i] - data[i + 1] - data[i + 2] > 90) {
            const j = ((y - 4) * size + x) * 4;
            points.push([x * scale, y * scale - 12.5]);
            colors.push([data[j], data[j + 1], data[j + 2]]);
            break;
          }
        }
      }

      const median = (values) => values.sort((a, b) => a - b)[values.length >> 1];
      const color = colors.length ? '#' + [0, 1, 2].map((c) => median(colors.map((rgb) => rgb[c])).toString(16).padStart(2, '0')).join('') : null;

      const solve = (pts) => {
        // y = A u² + B u + C, u = (x - 1000) / 1000
        const s = Array(5).fill(0);
        const r = Array(3).fill(0);
        pts.forEach(([x, y]) => {
          const u = (x - 1000) / 1000;
          for (let k = 0; k < 5; k++) s[k] += u ** k;
          for (let k = 0; k < 3; k++) r[k] += y * u ** k;
        });
        const m = [[s[4], s[3], s[2], r[2]], [s[3], s[2], s[1], r[1]], [s[2], s[1], s[0], r[0]]];
        for (let i = 0; i < 3; i++) {
          for (let k = i + 1; k < 3; k++) {
            const f = m[k][i] / m[i][i];
            for (let c = i; c < 4; c++) m[k][c] -= f * m[i][c];
          }
        }
        const C = m[2][3] / m[2][2];
        const B = (m[1][3] - m[1][2] * C) / m[1][1];
        const A = (m[0][3] - m[0][2] * C - m[0][1] * B) / m[0][0];
        return { A, B, C, at: (x) => A * ((x - 1000) / 1000) ** 2 + B * (x - 1000) / 1000 + C };
      };

      let coef = null;

      if (points.length >= 12) {
        let fit = solve(points);
        const inliers = points.filter(([x, y]) => Math.abs(fit.at(x) - y) < 8);

        if (inliers.length >= 12) fit = solve(inliers);

        const y = fit.at(this.config.center);

        if (Number.isFinite(y) && y > 1000 && y < 1550) {
          coef = [fit.A / 1e6, (fit.B - 2 * fit.A) / 1000, fit.A - fit.B + fit.C];
        }
      }

      return { coef, color };
    }

    // Standart kadraj dışındaki görsel için eğriyi bir kez ölç, bitince yeniden çiz
    ensureFit(combo, marker) {
      const src = this.assets[combo]?.base;

      if (!src || this.config.combos[combo] || marker.startsWith(BASE_MARKER) || src in this.fits) return;

      this.fits[src] = null;
      this.analyze(combo).then((result) => {
        this.fits[src] = result;

        if (result?.coef) this.scheduleRender();
      });
    }

    /* Seçim: [{ slot, value }] – slot kimliği animasyonların doğru charm'da oynaması için */

    selection() {
      const items = [];
      const letters = window.__awExtraLetters?.state?.letters || {};

      for (let slot = 1; slot <= 4; slot++) {
        if (letters[slot]) {
          items.push({ slot: String(slot), value: letters[slot] });
        }
      }

      return items;
    }

    // İp/charm rengi seçeneklerine renk noktası (tema kutulara değer kimliği verdiği için etiket metninden okunur)
    decorateSwatches() {
      const dots = {
        'kırmızı': 'kirmizi', red: 'kirmizi', siyah: 'siyah', black: 'siyah', beyaz: 'beyaz', white: 'beyaz',
        'sarı': 'sari', yellow: 'sari', turuncu: 'turuncu', orange: 'turuncu', mavi: 'mavi', blue: 'mavi',
        lacivert: 'lacivert', 'navy blue': 'lacivert', 'yeşil': 'yesil', green: 'yesil', bordo: 'bordo', 'claret red': 'bordo',
        pembe: 'pembe', pink: 'pembe', kahverengi: 'kahve', kahve: 'kahve', brown: 'kahve', gri: 'gri', grey: 'gri', gray: 'gri',
        'altın': 'altin', gold: 'altin', 'gümüş': 'gumus', silver: 'gumus', oksit: 'oksit', oxide: 'oksit'
      };

      this.section.querySelectorAll('variant-picker .block-swatch').forEach((label) => {
        const text = label.textContent.trim();
        const key = dots[text.toLocaleLowerCase('tr')];

        if (key) {
          if (label.dataset.awDot !== key) label.dataset.awDot = key;
          return;
        }

        // Listede olmayan yeni ip rengi: nokta rengi o rengin görselinden
        const combo = this.cords[text];

        if (!combo || label.dataset.awDot === 'auto') return;

        this.analyze(combo).then((result) => {
          if (!result?.color) return;
          label.style.setProperty('--aw-dot', result.color);
          label.dataset.awDot = 'auto';
        });
      });
    }

    /* Galeri */

    findGalleryImage(marker) {
      const images = [...this.section.querySelectorAll('.product-gallery__media img')];
      return images.find((image) => (image.currentSrc || image.src || '').includes(marker)) || null;
    }

    // Galeride yalnızca seçili kombinasyonun boş bileklik görseli görünsün (diğer ip/metal ve koleksiyon tanıtım görselleri gizlenir)
    filterGallery(marker) {
      const cells = [...this.section.querySelectorAll('.product-gallery__media')];
      const srcOf = (cell) => cell.querySelector('img')?.getAttribute('src') || '';

      if (!cells.some((cell) => srcOf(cell).includes(marker))) return;

      cells.forEach((cell) => {
        const hide = !srcOf(cell).includes(marker);

        if (hide === cell.hidden) return;

        cell.hidden = hide;
        this.section.querySelectorAll(`.product-gallery__thumbnail[data-media-id="${cell.getAttribute('data-media-id')}"]`).forEach((thumb) => {
          thumb.hidden = hide;
        });
      });
    }

    ensureGalleryLayer(marker) {
      const image = this.findGalleryImage(marker);

      // Varyant değişiminde önceki örnekten/görselden kalan katmanlar (eski charm'larla) temizlenir
      this.section.querySelectorAll('.product-gallery__media .aw-cp__layer').forEach((layer) => {
        if (layer !== this.galleryLayer || this.galleryImage !== image) layer.remove();
      });

      if (!image) {
        this.galleryLayer?.remove();
        this.galleryLayer = null;
        return null;
      }

      if (this.galleryLayer && this.galleryLayer.isConnected && this.galleryImage === image) {
        return this.galleryLayer;
      }

      this.galleryLayer?.remove();
      this.galleryImage = image;

      const container = image.parentElement;

      if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }

      this.galleryLayer = document.createElement('div');
      this.galleryLayer.className = 'aw-cp__layer';
      this.galleryLayer.setAttribute('aria-hidden', 'true');
      container.append(this.galleryLayer);

      this.resizeObserver.disconnect();
      this.resizeObserver.observe(image);
      this.positionGalleryLayer();

      return this.galleryLayer;
    }

    positionGalleryLayer() {
      const image = this.galleryImage;
      const layer = this.galleryLayer;

      if (!image || !layer) return;

      Object.assign(layer.style, {
        left: `${image.offsetLeft}px`,
        top: `${image.offsetTop}px`,
        width: `${image.offsetWidth}px`,
        height: `${image.offsetHeight}px`
      });

      this.updateLayout();
    }

    // Galeri bilgilerin yanında değilse (mobil, üstte kalıyor) blok içindeki küçük önizlemeyi göster
    updateLayout() {
      const image = this.galleryImage;
      let stacked = true;

      if (image && image.isConnected) {
        const imageRect = image.getBoundingClientRect();
        const ownRect = this.getBoundingClientRect();
        stacked = imageRect.width === 0 || ownRect.left < imageRect.right - 20;
      }

      this.toggleAttribute('stacked', stacked);
      this.updateFloat();
    }

    // Telefon/tablet: ürün görseli ekrandan çıktıysa ve seçim alanı (seçenekler → sepete ekle) ekrandaysa üstte sabit önizleme şeridi
    updateFloat() {
      if (!this.floatElement) return;

      let show = false;

      if (this.hasAttribute('stacked')) {
        const headerBottom = Math.max(0, document.querySelector('.shopify-section--header')?.getBoundingClientRect().bottom || 0);
        const gallery = this.section.querySelector('product-gallery');
        const start = this.section.querySelector('variant-picker') || this;
        const end = this.section.querySelector('buy-buttons') || this.section.querySelector('product-form');
        const galleryGone = !gallery || gallery.getBoundingClientRect().bottom < headerBottom + 40;
        const inBuilder = start.getBoundingClientRect().top < window.innerHeight && (!end || end.getBoundingClientRect().bottom > headerBottom + 140);
        show = galleryGone && inBuilder;
      }

      this.floatElement.hidden = !show;
    }

    updateCaption(count) {
      const caption = this.querySelector('[data-aw-cp-caption-side]');

      if (!caption) return;

      if (count === 0) {
        caption.textContent = 'Charm\'larınızı seçin, bileklikte canlı görün.';
      } else if (this.hasAttribute('stacked')) {
        caption.textContent = 'Canlı önizleme: seçtiğiniz charm\'lar bu görselde gösterilir.';
      } else {
        caption.textContent = 'Canlı önizleme: seçtiğiniz charm\'lar ürün görselinde gösterilir.';
      }
    }

    /* Çizim */

    poly(coefficients, x) {
      return coefficients.reduce((sum, c) => sum * x + c, 0);
    }

    // Charm'lar kesim genişliğine göre yan yana dizilir; aralarında config.gap px boşluk kalır (üst üste binmez)
    layout(items, combo) {
      const { values: valueMap, gap, center } = this.config;
      const { charms, coef } = this.geometry(combo);
      const derivative = coef.slice(0, -1).map((c, i, arr) => c * (arr.length - i));
      const placeable = items.map((item) => ({ ...item, key: valueMap[item.value] })).filter((item) => item.key && charms[item.key]);
      const total = placeable.reduce((sum, item) => sum + charms[item.key][2], 0) + gap * Math.max(placeable.length - 1, 0);
      let cursor = center - total / 2;

      return placeable.map((item) => {
        const [sx, sy, w, h, ax, ay] = charms[item.key];
        const cx = cursor + ax;
        const cy = this.poly(coef, cx);
        const angle = Math.atan(this.poly(derivative, cx)) * 180 / Math.PI;
        cursor += w + gap;

        return { ...item, sx, sy, w, h, ax, ay, cx, cy, angle };
      });
    }

    paint(layer, placed, combo) {
      if (!layer) return;

      const size = this.config.size;
      const [spriteW, spriteH] = this.geometry(combo).sprite;
      const sprite = this.assets[combo]?.sprite;
      const existing = new Map([...layer.children].map((child) => [child.dataset.slot, child]));

      placed.forEach((item) => {
        let element = existing.get(item.slot);
        existing.delete(item.slot);

        if (!element) {
          element = document.createElement('span');
          element.className = 'aw-cp__charm';
          element.dataset.slot = item.slot;
          layer.append(element);
        }

        if (element.dataset.key !== item.key || element.dataset.combo !== combo) {
          element.dataset.key = item.key;
          element.dataset.combo = combo;
          element.classList.remove('is-entering');
          void element.offsetWidth;
          element.classList.add('is-entering');
        }

        Object.assign(element.style, {
          left: `${(item.cx - item.ax) / size * 100}%`,
          top: `${(item.cy - item.ay) / size * 100}%`,
          width: `${item.w / size * 100}%`,
          height: `${item.h / size * 100}%`,
          backgroundImage: `url("${sprite}")`,
          backgroundSize: `${spriteW / item.w * 100}% ${spriteH / item.h * 100}%`,
          backgroundPosition: `${spriteW === item.w ? 0 : item.sx / (spriteW - item.w) * 100}% ${spriteH === item.h ? 0 : item.sy / (spriteH - item.h) * 100}%`,
          transformOrigin: `${item.ax / item.w * 100}% ${item.ay / item.h * 100}%`,
          transform: `rotate(${item.angle.toFixed(2)}deg)`
        });
      });

      existing.forEach((element) => element.remove());
    }

    render() {
      const combo = this.combo();
      const marker = this.assets[combo]?.marker || BASE_MARKER;
      this.ensureFit(combo, marker);
      const items = this.selection();
      const placed = this.layout(items, combo);
      const missing = items.map((item) => item.value).filter((value) => !this.config.values[value]);

      // Küçük önizleme ve alt şerit: seçili kombinasyonun boş bileklik görseli
      const baseSrc = this.assets[combo]?.base;

      if (baseSrc) {
        this.querySelectorAll('img[data-aw-cp-base]').forEach((image) => {
          if (image.getAttribute('src') !== baseSrc) {
            image.removeAttribute('srcset');
            image.setAttribute('src', baseSrc);
          }
        });
      }

      this.filterGallery(marker);
      this.decorateSwatches();

      this.layers.forEach((layer) => this.paint(layer, placed, combo));
      this.paint(this.ensureGalleryLayer(marker), placed, combo);
      this.updateLayout();
      this.updateCaption(items.length);

      const missingElement = this.querySelector('[data-aw-cp-missing]');

      if (missingElement) {
        missingElement.hidden = missing.length === 0;
        missingElement.textContent = missing.length ? `${missing.join(', ')} için önizleme görseli henüz yok; siparişinize eklenir.` : '';
      }
    }
  }

  if (!window.customElements.get('aw-charm-preview')) {
    window.customElements.define('aw-charm-preview', AwCharmPreview);
  }
})();
